import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  DatePicker,
  Switch,
  message,
  Breadcrumb,
  Divider,
  Row,
  Col,
  Spin,
  Table,
  InputNumber,
  Space,
  Typography,
  TimePicker,
  Alert,
  Upload,
  Radio,
  TreeSelect,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  UserOutlined,
  PlusOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  collectionAPIWithErrorHandling,
  collectionHelpers,
} from "../../../api/collection/collection";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";
import * as XLSX from 'xlsx';
import PaymentAccountSelector from "../PaymentAccount/PaymentAccountList";
import { paymentAccountAPIWithErrorHandling } from "../../../api/payment/paymentAccount";
import "../Collections/CollectionForm.css";

const { TextArea } = Input;
const { Text } = Typography;
const { RangePicker } = DatePicker;

export default function StoreCollectionPopulation() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [valueOrg, setValueOrg] = useState();
  const [treeData, setTreeData] = useState([]);

  const isSendZns = Form.useWatch("is_send_zns", form);

  const fees = [
    { id: "K1", name: "K1" },
    { id: "K2", name: "K2" },
    { id: "K3", name: "K3" }
  ];

  const collectionTypes = [
    { label: "Khách hàng", value: "CUSTOMER" },
    { label: "Bãi xe", value: "VEHICLE" },
    { label: "Chung cư", value: "APARTMENT" },
  ];
  const [uploading, setUploading] = useState(false);
  const [uploadedData, setUploadedData] = useState([]);
  const [fileList, setFileList] = useState([]);
  
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);

  // Fetch available customers on component mount
  useEffect(() => {
    loadOrganizations();
    fetchAccount();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response =
        await organizationAPIWithErrorHandling.getTreeStructure();
      if (response.success) {
        const rawData = response.data;
        const flatTree = flattenTreeData(rawData);
        setTreeData(flatTree); // Dùng cho TreeSelect
      }
    } catch (error) {
      message.error(error.message);
    }
  };
  
  const fetchAccount = async () => {
    try {
      const response = await paymentAccountAPIWithErrorHandling.getAccountAll();
      const groupData = response?.data || [];
      setAccounts(groupData);
      setSelectedAccount(null);
    } catch (error) {
      message.error("Không thể tải danh sách tài khoản: " + error.message);
    }
  };
  
  const handleSelectAccount = (account) => {
      setSelectedAccount(account);
      message.success(`Đã chọn tài khoản: ${account.name}`);
  };
  

  const flattenTreeData = (nodes, parentId = 0) => {
    let result = [];
    nodes.forEach((node) => {
      result.push({
        id: node.id,
        pId: parentId,
        value: String(node.id),
        title: node.name,
        isLeaf: node.children_recursive?.length === 0,
      });

      if (node.children_recursive && node.children_recursive.length > 0) {
        result = result.concat(
          flattenTreeData(node.children_recursive, node.id)
        );
      }
    });
    return result;
  };

  // Generate collection name based on date range
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      const startDate = dates[0];
      const month = startDate.month() + 1;
      const year = startDate.year();
      const suggestedName = collectionHelpers.generateCollectionName(month, year);
      const checkName = form.getFieldValue("name");
      if (!checkName) {
        form.setFieldValue("name", suggestedName);
      }
    }
  };

  const downloadTemplate = () => {
    // Tạo header động từ fees hiện tại
    const feeColumns = fees.reduce((obj, fee) => {
      obj[fee.name] = 50000; // Giá trị mẫu
      return obj;
    }, {});

    const template = [
      {
        'STT': 1,
        'Họ và tên': 'Nguyễn Văn A',
        'Số điện thoại': '0912345678',
        'Tài sản': 'A101',// Căn hộ,Xe
        ...feeColumns,
        'Giảm trừ': 0,
        'Tổng tiền': 100000
      },
      {
        'STT': 2,
        'Họ và tên': 'Trần Thị B',
        'Số điện thoại': '0987654321',
        'Tài sản': 'B202', // Căn hộ,Xe
        ...feeColumns,
        'Giảm trừ': 5000,
        'Tổng tiền': 95000
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mẫu dữ liệu");

    // Tự động điều chỉnh độ rộng cột
    const colWidths = [
      { wch: 5 },  // STT
      { wch: 20 }, // Họ tên
      { wch: 15 }, // SĐT
      { wch: 10 }, // Tài sản/Căn hộ
      ...fees.map(() => ({ wch: 12 })), // Các khoản thu
      { wch: 12 }, // Giảm trừ
      { wch: 15 }  // Tổng tiền
    ];
    ws['!cols'] = colWidths;

    XLSX.writeFile(wb, `Mau_du_lieu_dot_thu_${dayjs().format('YYYYMMDD')}.xlsx`);
    message.success("Đã tải file mẫu thành công!");
  };

  const handleFileUpload = (file) => {
    setUploading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          message.error('File không có dữ liệu!');
          setUploading(false);
          return;
        }

        // Validate cấu trúc dữ liệu
        // const requiredColumns = ['Họ và tên', 'Số điện thoại', 'Căn hộ'];
        const requiredColumns = ['Họ và tên', 'Số điện thoại', 'Tài sản'];
        const firstRow = jsonData[0];
        const missingColumns = requiredColumns.filter(col => !(col in firstRow));

        if (missingColumns.length > 0) {
          message.error(`File thiếu các cột: ${missingColumns.join(', ')}`);
          setUploading(false);
          return;
        }

        // Xử lý dữ liệu và thêm key unique
        const processedData = jsonData.map((row, index) => ({
          key: `customer-${index}`,
          stt: index + 1,
          name: row['Họ và tên'] || '',
          phone: String(row['Số điện thoại'] || ''),
          // apartment: row['Căn hộ'] || '',
          apartment: row['Tài sản'] || '',
          // Lấy tất cả các cột phí (không phải cột cố định)
          fees: Object.keys(row).reduce((acc, key) => {
            // if (!['STT', 'Họ và tên', 'Số điện thoại', 'Căn hộ', 'Giảm trừ', 'Tổng tiền'].includes(key)) {
            if (!['STT', 'Họ và tên', 'Số điện thoại', 'Tài sản', 'Giảm trừ', 'Tổng tiền'].includes(key)) {
              acc[key] = Number(row[key]) || 0;
            }
            return acc;
          }, {}),
          discount: Number(row['Giảm trừ']) || 0,
          total: Number(row['Tổng tiền']) || 0,
          // total: Number((Object.values(fees).reduce((sum, v) => sum + v, 0) - (Number(row['Giảm trừ']) || 0))) || 0
        }));

        setUploadedData(processedData);
        setFileList([file]);
        message.success(`Import thành công ${processedData.length} khách hàng!`);
      } catch (error) {
        console.error('Error parsing file:', error);
        message.error('Lỗi khi đọc file. Vui lòng kiểm tra định dạng file!');
      } finally {
        setUploading(false);
      }
    };

    reader.onerror = () => {
      message.error('Lỗi khi đọc file!');
      setUploading(false);
    };

    reader.readAsArrayBuffer(file);
    return false; // Prevent auto upload
  };

  const handleRemoveFile = () => {
    setFileList([]);
    setUploadedData([]);
    message.info('Đã xóa dữ liệu import');
  };

  // Tạo columns động cho bảng dựa trên dữ liệu upload
  const getTableColumns = () => {
    const baseColumns = [
      {
        title: 'STT',
        dataIndex: 'stt',
        key: 'stt',
        width: 60,
        fixed: 'left',
        align: 'center',
      },
      {
        title: 'Họ và tên',
        dataIndex: 'name',
        key: 'name',
        width: 180,
        fixed: 'left',
      },
      {
        title: 'Số điện thoại',
        dataIndex: 'phone',
        key: 'phone',
        width: 130,
      },
      {
        // title: 'Căn hộ',
        title: 'Tài sản',
        dataIndex: 'apartment',
        key: 'apartment',
        width: 100,
      },
    ];

    // Lấy danh sách các khoản phí từ dữ liệu đầu tiên
    const feeColumns = uploadedData.length > 0
      ? Object.keys(uploadedData[0].fees).map(feeName => ({
        title: feeName,
        key: feeName,
        width: 120,
        align: 'right',
        render: (_, record) => (
          <span>{record.fees[feeName]?.toLocaleString('vi-VN')} ₫</span>
        ),
      }))
      : [];

    const endColumns = [
      {
        title: 'Giảm trừ',
        dataIndex: 'discount',
        key: 'discount',
        width: 120,
        align: 'right',
        render: (value) => <span>{value?.toLocaleString('vi-VN')} ₫</span>,
      },
      {
        title: 'Tổng tiền',
        dataIndex: 'total',
        key: 'total',
        width: 140,
        align: 'right',
        fixed: 'right',
        render: (value) => (
          <Text strong style={{ color: '#0070D7' }}>
            {value?.toLocaleString('vi-VN')} ₫
          </Text>
        ),
      },
    ];

    return [...baseColumns, ...feeColumns, ...endColumns];
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);

      // Validate uploaded file
      if (fileList.length === 0) {
        message.error("Vui lòng upload file Excel/CSV!");
        setSubmitting(false);
        return;
      }

      // Validate uploaded data
      if (uploadedData.length === 0) {
        message.error("File không có dữ liệu hợp lệ!");
        setSubmitting(false);
        return;
      }

      if (!selectedAccount) {
        message.error("Vui lòng chọn tài khoản thanh toán cho khách hàng!");
        setSubmitting(false);
        return;
      }

      // Prepare FormData
      const formData = new FormData();

      // Basic fields
      formData.append('name', values.name);
      formData.append('description', values.description || '');

      // Date range
      if (values.dateRange && values.dateRange.length === 2) {
        formData.append('start_date', values.dateRange[0].format('YYYY-MM-DD'));
        formData.append('end_date', values.dateRange[1].format('YYYY-MM-DD'));
      }

      // ZNS configuration
      formData.append('is_send_zns', values.is_send_zns ? '1' : '0');

      if (values.send_schedule_at) {
        formData.append('send_schedule_at', values.send_schedule_at.format('HH:mm:ss'));
      }

      if (values.send_date) {
        formData.append('send_date', values.send_date.format('YYYY-MM-DD'));
      }

      if (values.before_date !== undefined && values.before_date !== null) {
        formData.append('before_date', values.before_date.toString());
      }

      if (values.after_date !== undefined && values.after_date !== null) {
        formData.append('after_date', values.after_date.toString());
      }

      // Organization
      formData.append('organization_id', values.organization_id);

      // Collection type -> charge_target_type
      formData.append('charge_target_type', values.collection_type);

      formData.append('acc_payment_id', selectedAccount.id);

      // File upload
      formData.append('file', fileList[0]);

      // console.log('Submitting data:', {
      //   name: values.name,
      //   organization_id: values.organization_id,
      //   charge_target_type: values.collection_type,
      //   file: fileList[0].name,
      //   uploadedRecords: uploadedData.length
      // });

      await collectionAPIWithErrorHandling.importCollectionTarget(formData);

      message.success("Tạo đợt thu thành công!");

      // Navigate back after success
      setTimeout(() => {
        navigate("/collections");
      }, 1200);

    } catch (error) {
      console.error('Submit error:', error);
      message.error("Lỗi khi tạo đợt thu: " + (error.message || "Vui lòng thử lại"));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.resetFields();
    setUploadedData([]);
    setFileList([]);
    message.info("Đã xóa tất cả dữ liệu nhập");
  };

  // Handle back navigation
  const handleBack = () => {
    navigate("/collections");
    message.info("Quay lại danh sách đợt thu");
  };

  const disabledDate = current => {
    // Can not select days before today and today
    return current && current < dayjs().endOf('day');
  };

  return (
    <div className="collection-form-page">
      <div className="collection-page-header">
        <Breadcrumb
          separator=">"
          items={[
            {
              href: "/",
              title: (
                <>
                  <HomeOutlined /> Trang chủ
                </>
              ),
            },
            {
              href: "/collections",
              title: (
                <>
                  <DollarOutlined /> Đợt thu
                </>
              ),
            },
            {
              title: (
                <>
                  <PlusOutlined /> Thêm mới (Nâng cao Excel)
                </>
              ),
            },
          ]}
        />
        <p>Import khách hàng từ file Excel/CSV để tạo đợt thu nhanh chóng</p>
      </div>

        {/* Form */}
        <Spin spinning={submitting} tip="Đang lưu thông tin...">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark="optional"
            initialValues={{
              status: "PLANNED",
              is_send_zns: true,
              collection_type: "APARTMENT"
            }}
          >
            {/* Basic Information */}
            <Row gutter={[24, 16]}>
              <Col xs={24} lg={16}>
                <Card
                  title={
                    <span>
                      <span className="collection-card-icon blue"><CalendarOutlined /></span>
                      Thông tin đợt thu
                    </span>
                  }
                  className="collection-section-card collection-card-blue h-full"
                >
                  <Row gutter={16}>
                    <Col xs={24} md={24}>
                      <Form.Item
                        name="name"
                        label={<span className="font-medium">Tên đợt thu</span>}
                        rules={[
                          { required: true, message: "Vui lòng nhập tên đợt thu" },
                          { max: 200, message: "Tên không được vượt quá 200 ký tự" },
                        ]}
                      >
                        <Input
                          placeholder="VD: Đợt thu tháng 11/2025"
                          className="rounded-lg"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                  <Form.Item
                    name="dateRange"
                    label={<span className="font-medium">Thời gian thu</span>}
                    rules={[{ required: true, message: "Vui lòng chọn thời gian thu!" }]}
                  >
                    <RangePicker
                      className="w-full"
                      format="DD/MM/YYYY"
                      placeholder={["Ngày bắt đầu", "Ngày kết thúc"]}
                      style={{ width: "100%" }}
                      disabledDate={disabledDate}
                      onChange={handleDateRangeChange}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="send_date"
                    label={<span className="font-medium">Ngày hiển thị gửi tin</span>}
                  >
                    <DatePicker
                      className="w-full"
                      format="DD/MM/YYYY"
                      placeholder="Chọn ngày"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>

                    <Col xs={24} md={24}>
                      <Form.Item
                        name="description"
                        label={<span className="font-medium">Mô tả đợt thu</span>}
                      >
                        <TextArea
                          placeholder="Mô tả chi tiết về đợt thu này..."
                          rows={3}
                          className="rounded-lg"
                          maxLength={1000}
                          showCount
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        name="collection_type"
                        label="Nhóm đối tượng"
                        rules={[{ required: true, message: "Vui lòng chọn nhóm đối tượng" }]}
                      >
                        <Radio.Group
                          // defaultValue="APARTMENT"
                        >
                          {collectionTypes.map(item => (
                            <Radio key={item.value} value={item.value}>
                              {item.label}
                            </Radio>
                          ))}
                        </Radio.Group>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item label="Đơn vị"
                        name="organization_id"
                        rules={[{ required: true, message: "Vui lòng chọn đơn vị trực thuộc" }]}>
                        <TreeSelect
                          treeDataSimpleMode
                          value={valueOrg}
                          placeholder="Chọn đơn vị"
                          onChange={setValueOrg}
                          treeData={treeData}
                          showSearch
                          size="default"
                          filterTreeNode={(input, item) =>
                            item.title.toLowerCase().includes(input.toLowerCase())
                          }
                          allowClear
                        />
                      </Form.Item>
                    </Col>

                  </Row>
                </Card>
              </Col>

              <Col xs={24} lg={8}>
                <Card
                  title={
                    <span>
                      <span className="collection-card-icon green"><EnvironmentOutlined /></span>
                      Cấu hình gửi tin
                    </span>
                  }
                  className="collection-section-card collection-card-green h-full"
                >

                  <Form.Item
                    name="is_send_zns"
                    label={<span className="font-medium">Tin thông báo</span>}
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Bật" unCheckedChildren="Tắt" defaultChecked />
                  </Form.Item>

                  <Form.Item
                    name="send_schedule_at"
                    label={<span className="font-medium">Giờ gửi</span>}

                  >
                    <TimePicker
                      style={{ width: "100%" }}
                      placeholder="Chọn giờ gửi"
                      // onChange={onChangeSendScheduleAt}
                      // disabled={!isSendZns}
                      disabled
                    />
                  </Form.Item>

                  <Form.Item
                    name="before_date"
                    label={<span className="font-medium">Trước hạn</span>}
                    rules={[{ type: "number", min: 0, message: "≥ 0" }]}
                  >
                    <InputNumber
                      min={0}
                      placeholder="Số ngày"
                      style={{ width: "100%" }}
                      addonAfter="ngày"
                      // disabled={!isSendZns}
                      disabled
                    />
                  </Form.Item>

                  <Form.Item
                    name="after_date"
                    label={<span className="font-medium">Sau hạn</span>}
                    rules={[{ type: "number", min: 1, message: "≥ 1" }]}
                  >
                    <InputNumber
                      min={1}
                      placeholder="Số ngày"
                      style={{ width: "100%" }}
                      addonAfter="ngày"
                      // disabled={!isSendZns}
                      disabled
                    />
                  </Form.Item>

                  <Alert
                    message="Tin nhắn từ 22:00-06:00 sẽ được gửi vào sáng hôm sau"
                    type="info"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    style={{ fontSize: 12 }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Customer Selection */}
            <Row gutter={[24, 16]} style={{ marginTop: 20 }}>
              <Col xs={24} lg={16}>
            <Card
              className="collection-section-card collection-card-purple"
              title={
                <span>
                  <span className="collection-card-icon purple"><UserOutlined /></span>
                  Thêm khách hàng
                </span>
              }
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12} md={8} lg={12} xl={12}>
                    <Upload
                      beforeUpload={handleFileUpload}
                      accept=".xlsx,.xls,.csv"
                      fileList={fileList}
                      onRemove={handleRemoveFile}
                    // disabled={!valueOrg}
                    style={{width:"100%"}}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        loading={uploading}
                        // disabled={!valueOrg || fileList.length > 0}
                        block
                      >
                        {uploading ? 'Đang import...' : 'Upload File Excel/CSV'}
                      </Button>
                    </Upload>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={12} xl={12}>
                    <Button
                      type="dashed"
                      icon={<DownloadOutlined />}
                      onClick={downloadTemplate}
                      block
                    >
                      Tải file mẫu
                    </Button>
                  </Col>

                  {uploadedData.length > 0 && (
                    <Col xs={24} sm={24} md={8} lg={24} xl={24}>
                      <Alert
                        message={`Đã import ${uploadedData.length} khách hàng từ file`}
                        type="success"
                        showIcon
                        closable={false}
                      />
                    </Col>
                  )}
                </Row>
              </Space>
            </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card 
                  className={`collection-section-card collection-payment-card ${!selectedAccount ? 'collection-payment-card-warning' : ''}`}
                >
                  {!selectedAccount && (
                    <Alert
                      message="Vui lòng chọn tài khoản thanh toán trước khi thêm khách hàng"
                      type="warning"
                      showIcon
                      style={{ fontSize: 12, marginBottom: 14 }}
                    />
                  )}
                  <PaymentAccountSelector
                    accounts={accounts}
                    onSelect={handleSelectAccount}
                    selectedAccountId={selectedAccount?.id}
                  />
                </Card>
              </Col>
            </Row>

            {/* Customer Fees Table */}
            <Card
              className="collection-section-card collection-card-orange"
              style={{ marginTop: 20 }}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>
                    <span className="collection-card-icon orange"><DollarOutlined /></span>
                    Chi tiết các khoản thu
                  </span>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Tổng: {uploadedData.length} khách hàng
                  </Text>
                </div>
              }
            >
              {uploadedData.length > 0 ? (
                <>
                  <Table
                    columns={getTableColumns()}
                    dataSource={uploadedData}
                    // scroll={{ x: 1500, y: 500 }}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                      // default*: ghim pageSize thì chọn số dòng/trang không có tác dụng
                      defaultPageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Tổng ${total} khách hàng`,
                      pageSizeOptions: ['10', '20', '50', '100'],
                    }}
                    bordered
                    size="small"
                  />

                  {/* Summary Bar - Tổng kết */}
                  {(() => {
                    let grandSubtotal = 0;
                    let grandDiscount = 0;
                    let grandTotal = 0;

                    uploadedData.forEach(row => {
                      const feeSum = Object.values(row.fees || {}).reduce((sum, v) => sum + (Number(v) || 0), 0);
                      grandSubtotal += feeSum;
                      grandDiscount += Number(row.discount) || 0;
                      grandTotal += Number(row.total) || 0;
                    });

                    return (
                      <div className="collection-summary">
                        <Row gutter={[24, 12]} align="middle">
                          <Col xs={24} sm={6}>
                            <div className="collection-summary-stat">
                              <div className="stat-label">Số lượng</div>
                              <div className="stat-value blue">
                                <UserOutlined style={{ marginRight: 4 }} />
                                {uploadedData.length}
                                <span style={{ fontSize: 12, fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>khách hàng</span>
                              </div>
                            </div>
                          </Col>
                          <Col xs={24} sm={6}>
                            <div className="collection-summary-stat">
                              <div className="stat-label">Tổng phí</div>
                              <div className="stat-value green">
                                {grandSubtotal.toLocaleString('vi-VN')} đ
                              </div>
                            </div>
                          </Col>
                          <Col xs={24} sm={6}>
                            <div className="collection-summary-stat">
                              <div className="stat-label">Tổng giảm trừ</div>
                              <div className="stat-value orange">
                                -{grandDiscount.toLocaleString('vi-VN')} đ
                              </div>
                            </div>
                          </Col>
                          <Col xs={24} sm={6}>
                            <div className="collection-summary-highlight">
                              <div className="stat-label">Tổng tiền thu</div>
                              <div className="stat-value">
                                <DollarOutlined style={{ marginRight: 4 }} />
                                {grandTotal.toLocaleString('vi-VN')} đ
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="collection-empty-state">
                  <UploadOutlined />
                  <h4>Chưa có dữ liệu</h4>
                  <p>Vui lòng upload file Excel/CSV hoặc tải file mẫu để bắt đầu</p>
                </div>
              )}
            </Card>

            {/* Action Buttons */}
            <div className="collection-actions">
              <Row justify="space-between" align="middle">
                <Col>
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBack}
                    className="collection-btn-back"
                  >
                    Quay lại
                  </Button>
                </Col>
                <Col>
                  <Space>
                    <Button
                      type="default"
                      onClick={handleReset}
                      className="collection-btn-reset"
                    >
                      Làm mới
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={submitting}
                      icon={<SaveOutlined />}
                      className="collection-btn-submit"
                    >
                      {submitting ? "Đang lưu..." : "Lưu thông tin"}
                    </Button>
                  </Space>
                </Col>
              </Row>
            </div>
          </Form>
        </Spin>
      </div>
  );
}